import React, { useState, useEffect } from "react";
import { TableBuilder, TableBuilderColumn } from "baseui/table-semantic";
import { useHistory, useParams } from "react-router-dom";
import { connect } from "react-redux";
import {
  fetchModelRuns,
  deleteModelRun,
  fetchModelRunById,
} from "../../redux/model-run/ModelRunThunk";
import LoadingOverlay from "../../components/loading-overlay/LoadingOverlayComponent";
import { MODELRUN_ROW_MENU_ACTIONS } from "../../constants/AppEnums";
import { format, set } from "date-fns";
import { Button, SHAPE, SIZE as BSIZE, KIND } from "baseui/button";
import Overflow from "baseui/icon/overflow";
import { StatefulMenu } from "baseui/menu";
import { StatefulPopover, PLACEMENT } from "baseui/popover";
import { Tag, SIZE as TSIZE, VARIANT } from "baseui/tag";
import { Block } from "baseui/block";
import { MODELRUN_ROW_MENU_ITEMS } from "../../constants/AppEnums";
import {
  MODELRUN_TABLE_RUN_NAME,
  MODELRUN_TABLE_RUN_STATUS,
  MODELRUN_TABLE_RUN_CREATEDDATE,
} from "../../constants/ModelersConstants";
import useToaster from "../../hooks/useToaster";
import { FaCloudDownloadAlt } from "react-icons/fa"; // Add FaTrash for delete button
import useFileDownloader from "../../hooks/useFileDownloader";
import { Spinner } from "baseui/icon";
import useRouteParams from "../../hooks/useRouteParams";
import { LabelMedium, LabelSmall } from "baseui/typography";
import { styled } from "baseui";
import PaginatedTable from "../../components/paginated-table/PaginatedTable";
const TableAndPaginationContainer = styled("div", {
  maxWidth: "1000px", // Increase the maxWidth of the parent container
  display: "flex",
  minHeight: "65vh", // This ensures that the container has a minimum height
  margin: "0 auto",
  overflow: "hidden",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "space-between", // Pushes the footer to the bottom
  padding: "0 24px",
});

function ThreeDotActions({
  onItemAction,
  cellValue,
  rowId,
  selectedModelRunId,
}) {
  if (rowId === selectedModelRunId) {
    return <Block>{cellValue}</Block>; // Only display the cell value without the ellipsis button
  }
  return (
    <Block>
      <StatefulPopover
        placement={PLACEMENT.bottomLeft}
        content={({ close }) => (
          <StatefulMenu
            items={MODELRUN_ROW_MENU_ITEMS}
            onItemSelect={(item) => {
              onItemAction(item);
              close();
            }}
            overrides={{
              List: { style: { width: "100px" } },
            }}
          />
        )}
      >
        <Button shape="square" kind={KIND.secondary} size={BSIZE.compact}>
          <Overflow size={18} />
        </Button>
      </StatefulPopover>
      {cellValue}
    </Block>
  );
}
function statusToTagKind(status) {
  switch (status) {
    case "CREATED": {
      return "positive";
    }
    case "READY": {
      return "positive";
    }
    case "COMPLETED": {
      return "negative";
    }
    default: {
      return "neutral";
    }
  }
}

function DownloadCell({ generated, modelerId, modelRunId }) {
  const [downloadFile, loading] = useFileDownloader();
  return (
    <Button
      size={BSIZE.mini}
      disabled={!generated}
      startEnhancer={() =>
        loading ? <Spinner></Spinner> : <FaCloudDownloadAlt size={20} />
      }
      shape={SHAPE.pill}
      onClick={() => {
        downloadFile(
          "modeler",
          `${modelerId}/${modelRunId}.zip`,
          `${modelRunId}.zip`
        );
      }}
    >
      {" "}
      Download{" "}
    </Button>
  );
}
const PAGE_SIZE = 10;

function ModelerHistorySummary(props) {
  const history = useHistory();
  const { _, searchParams, searchPart } = useRouteParams();
  const { modelerId } = useParams();
  const { onFetchModelRunsByModelerId } = props;

  useToaster(props.fetchErrorMessage);
  useEffect(() => {
    onFetchModelRunsByModelerId(modelerId, PAGE_SIZE, null);
  }, [modelerId, searchPart]);

  const handleFetchData = (token) => {
    props.onFetchModelRunsByModelerId(modelerId, PAGE_SIZE, token);
  };
  const columns = [
    {
      header: "  ",
      cell: (row) => (
        <ThreeDotActions
          onItemAction={(action) => {
            if (action.item.action === MODELRUN_ROW_MENU_ACTIONS.SELECT) {
              // props.setActiveRun(row);
              history.push({
                search: row?.id ? `?runId=${row?.id}` : "",
              });
            }

            if (action.item.action === MODELRUN_ROW_MENU_ACTIONS.DELETE) {
              props.onDeleteModelRunById(row.id);
              // props.setActiveRun(null);
            }
          }}
          cellValue={row?.itemName}
          rowId={row?.id}
          selectedModelRunId={searchParams["runId"]}
        />
      ),
    },
    {
      header: "Name",
      cell: (row) => <LabelMedium>{row.runName}</LabelMedium>,
    },
    {
      header: "Status",
      cell: (row) => (
        <Tag
          closeable={false}
          variant={VARIANT.solid}
          kind={statusToTagKind(row.runStatus)}
        >
          {row.runStatus}
        </Tag>
      ),
    },
    {
      header: "Created Date",
      cell: (row) => (
        <LabelSmall>
          {format(new Date(row.createdAt), "yyyy-MM-dd - HH:mm")}
        </LabelSmall>
      ),
    },
    {
      header: " ",
      cell: (row) => (
        <DownloadCell
          generated={row.generated}
          modelerId={modelerId}
          modelRunId={row.id}
        />
      ),
    },
  ];

  return (
    <Block>
      <LoadingOverlay isLoading={props.fetchInProgress} />

      <TableAndPaginationContainer>
        <PaginatedTable
          data={props.modelRuns}
          columns={columns}
          nextToken={props.nextToken}
          onNext={handleFetchData}
          onPrev={handleFetchData}
        />
      </TableAndPaginationContainer>
    </Block>
  );
}
const mapStateToProps = (state) => {
  return {
    modelRuns: state.modelRun.modelRuns,
    selectedModelRun: state.modelRun.selectedModelRun,
    fetchInProgress: state.modelRun.fetchInProgress,
    fetchError: state.modelRun.fetchError,
    fetchErrorMessage: state.modelRun.fetchErrorMessage,
    nextToken: state.modelRun.nextToken,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    onFetchModelRunsByModelerId: (modelerId, size, nextToken) => {
      dispatch(fetchModelRuns(modelerId, size, nextToken));
    },
    onDeleteModelRunById: (modelerRunId) => {
      dispatch(deleteModelRun(modelerRunId));
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ModelerHistorySummary);
